// Subida de soportes (comprobantes) de las gestiones de PQR.
// Los archivos van a Storage y en Firestore solo se guarda la referencia.

import { storage } from './db'

export const MAX_BYTES = 10 * 1024 * 1024 // 10 MB

// Tope de archivos por gestion. Evita que un solo guardado dispare decenas de
// subidas en paralelo y deje la pantalla colgada.
export const MAX_ARCHIVOS = 10

const EXTENSIONES_IMAGEN = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic']
const EXTENSIONES_VIDEO = ['mp4', 'mov', 'webm', 'm4v']

const EXTENSIONES_PERMITIDAS = [
  ...EXTENSIONES_IMAGEN,
  ...EXTENSIONES_VIDEO,
  'pdf',
  'doc', 'docx',
  'xls', 'xlsx',
  'msg', 'eml'
]

export const ACCEPT_ATTR =
  'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.msg,.eml'

function extension (nombre) {
  const partes = (nombre || '').split('.')
  return partes.length > 1 ? partes.pop().toLowerCase() : ''
}

// Devuelve un mensaje de error, o null si el archivo es valido.
export function validarArchivo (file) {
  if (!file) return 'Archivo invalido'
  if (file.size > MAX_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1)
    return `"${file.name}" pesa ${mb} MB. El maximo por archivo es 10 MB.`
  }
  // Arrastrar una carpeta, o un archivo que quedo a medio copiar, llega con
  // cero bytes: Storage lo acepta y despues el soporte no sirve para nada.
  if (!file.size) {
    return `"${file.name}" esta vacio (0 bytes).`
  }
  if (!EXTENSIONES_PERMITIDAS.includes(extension(file.name))) {
    return `"${file.name}" no es un tipo de archivo permitido.`
  }
  return null
}

// Valida el lote completo ANTES de subir nada, asi un archivo invalido no deja
// a los otros ya subidos como huerfanos en Storage.
// Devuelve un mensaje de error, o null si el lote es valido.
export function validarLote (archivos) {
  const lista = archivos || []
  if (lista.length > MAX_ARCHIVOS) {
    return `Podes adjuntar hasta ${MAX_ARCHIVOS} archivos por gestion.`
  }
  for (const file of lista) {
    const error = validarArchivo(file)
    if (error) return error
  }
  return null
}

// Nombre seguro para Storage, conservando el original para mostrar.
function rutaDestino (pqrId, file, indice, carpeta) {
  const limpio = (file.name || 'archivo')
    .replace(/[^\w.-]+/g, '_')
    .slice(-80)
  return `${carpeta}/${pqrId}/${Date.now()}_${indice}_${limpio}`
}

export async function subirAdjunto (pqrId, file, indice = 0, carpeta = 'pqrs_externos') {
  const ref = storage.ref(rutaDestino(pqrId, file, indice, carpeta))
  const snapshot = await ref.put(file)
  const url = await snapshot.ref.getDownloadURL()

  return {
    nombre: file.name,
    url,
    ruta: ref.fullPath,
    tipo: file.type || '',
    tamano: file.size
  }
}

// Borra soportes de Storage. Es best-effort: si alguno ya no esta, o Storage
// rechaza el borrado, no tiene sentido romper el flujo que la llamo.
export async function borrarAdjuntos (adjuntos) {
  const lista = (adjuntos || []).filter(a => a && a.ruta)
  await Promise.all(lista.map(a =>
    storage.ref(a.ruta).delete().catch(() => null)
  ))
}

// Sube el lote completo o ninguno: si uno falla, se borran los que si subieron
// para no dejar huerfanos en Storage.
export async function subirAdjuntos (pqrId, archivos, carpeta = 'pqrs_externos') {
  const lista = archivos || []

  const resultados = await Promise.all(lista.map((file, i) =>
    subirAdjunto(pqrId, file, i, carpeta).then(
      adjunto => ({ ok: true, adjunto }),
      error => ({ ok: false, error })
    )
  ))

  const subidos = resultados.filter(r => r.ok).map(r => r.adjunto)
  const fallido = resultados.find(r => !r.ok)

  if (fallido) {
    await borrarAdjuntos(subidos)
    throw fallido.error
  }

  return subidos
}

// Como se puede mostrar el adjunto: los de Office no tienen visor posible
// en el navegador, asi que se descargan.
export function tipoVisor (adjunto) {
  const ext = extension(adjunto && adjunto.nombre)
  if (EXTENSIONES_IMAGEN.includes(ext)) return 'imagen'
  if (EXTENSIONES_VIDEO.includes(ext)) return 'video'
  if (ext === 'pdf') return 'pdf'
  return 'descarga'
}

// Icono de Material Icons segun el tipo, para mostrar el chip del adjunto.
export function iconoAdjunto (adjunto) {
  const ext = extension(adjunto && adjunto.nombre)
  if (EXTENSIONES_IMAGEN.includes(ext)) return 'image'
  if (EXTENSIONES_VIDEO.includes(ext)) return 'videocam'
  if (ext === 'pdf') return 'picture_as_pdf'
  if (['doc', 'docx'].includes(ext)) return 'description'
  if (['xls', 'xlsx'].includes(ext)) return 'table_chart'
  if (['msg', 'eml'].includes(ext)) return 'mail'
  return 'attach_file'
}

export function tamanoLegible (bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
